/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Libs
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

// Mantine
import {
	ActionIcon,
	Badge,
	Box,
	Flex,
	Group,
	Image,
	Paper,
	Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

// UI Components
import { Title } from '@components/v2/ui';
import { Datatable } from '@components/v2/ui/Datatable/Datatable';
import { Pagination } from '@components/v2/ui/Pagination';
import { ProjectCard } from '@components/v2/ui/ProjectCard';
import { ProjectCardSkeleton } from '@components/v2/ui/ProjectCard/ProjectCardSkeleton';
import {
	TViewMode,
	ViewToggle,
} from '@components/v2/ui/ViewToggle/ViewToggle';

// Features
import { useMeasurabilityProjects } from '@features/catalogs/meausrability-projects/list/api/get-public-measurability-projects';
import { tableSchemaBase as columns } from '@features/catalogs/meausrability-projects/list/catalog-public-measurability.columns';
import { Filter } from '@features/catalogs/meausrability-projects/list/components/Filter-public-catalog';
import { useMeasurabilityProject } from '@features/catalogs/meausrability-projects/list/hooks/useMeasurabilityProjects';

// Icons
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import { IconFilter } from '@tabler/icons-react';

// Constants
import { NOT_APPLICABLE } from '@config/constants/common';

// Styles
import classes from './measurability-projects.module.css';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const MeasurabilityProjectsPublic = () => {
	/* ********************************** Hooks ********************************* */
	const { t } = useTranslation();
	const navigate = useNavigate();

	// Custom hook for measurability project logic
	const {
		handleChangePaginationSize,
		handleChangePagination,
		measurabilityProjectsPage,
		measurabilityProjectsPageSize,
		measurabilityProjectsQueryParams,
	} = useMeasurabilityProject();

	// APIs
	const {
		data: measurabilityProjects,
		total: totalItems,
		totalPages,
		isPending,
	} = useMeasurabilityProjects(
		measurabilityProjectsPage,
		measurabilityProjectsPageSize,
		measurabilityProjectsQueryParams
	);

	/* ******************************** States ********************************** */
	const [mode, setMode] = useState<TViewMode>('grid');
	const [
		openedFilters,
		{ open: openFilters, close: closeFilters },
	] = useDisclosure(false);
	const [filtersNbr, setFiltersNbr] = useState(0);

	/* ******************************** RENDERING ******************************* */
	return (
		<Paper className={classes['main-layout']}>
			<Filter
				opened={openedFilters}
				onClose={closeFilters}
				title={t('filters.titles.projects')}
				onApplyFilters={(filters) => {
					setFiltersNbr(Object.keys(filters).length);
				}}
				value={measurabilityProjectsQueryParams}
			/>

			{/* Header */}
			<Box className={classes.header}>
				<Title
					c="var(--mantine-color-igluPrimary-9)"
					order={4}
					textWrap="wrap"
					data-testid="topbar-title"
				>
					{t('global.measurabilities')}
				</Title>
			</Box>

			{/* Content */}
			<Paper ml="20px" mr="20px" mt="24px" p="12px">
				{/* Action bar */}
				<Box className={classes['action-bar']}>
					<Flex
						flex={1}
						justify="space-between"
						wrap="wrap"
					>
						<ViewToggle mode={mode} onChange={setMode} />
						<Group gap="var(--mantine-spacing-xs)">
							<div style={{ position: 'relative' }}>
								{filtersNbr > 0 && (
									<Badge
										style={{
											position: 'absolute',
											zIndex: '3',
											right: '-3px',
										}}
										color="var(--mantine-color-igluRed-9)"
										size="xs"
										circle
									>
										{filtersNbr}
									</Badge>
								)}
								<ActionIcon
									variant="outline"
									size="48px"
									radius="xl"
									color="var(--mantine-color-igluBorder-9)"
									data-testid="bell-icon-button"
									onClick={openFilters}
								>
									<IconFilter
										color="var(--mantine-color-igluPrimary-9)"
										radius="xl"
										size={24}
										stroke={1.5}
									/>
								</ActionIcon>
							</div>
						</Group>
					</Flex>
				</Box>

				{/* Grid mode */}
				{mode === 'grid' && (
					<Box
						style={{
							display: 'grid',
							gridTemplateColumns:
								'repeat(auto-fill, minmax(300px, 1fr))',
							gap: 'var(--mantine-spacing-sm)',
						}}
					>
						{isPending
							? [...Array(10).keys()].map((_, index) => (
									<AnimatePresence
										key={index}
										mode="wait"
									>
										<motion.div
											key="empty"
											initial={{ y: 10, opacity: 0 }}
											animate={{ y: 0, opacity: 1 }}
											exit={{ y: -10, opacity: 0 }}
											transition={{ duration: 0.2 }}
											style={{
												display: 'flex',
												flex: 1,
											}}
										>
											<ProjectCardSkeleton fullWidth />
										</motion.div>
									</AnimatePresence>
								))
							: measurabilityProjects &&
								Array.isArray(measurabilityProjects) &&
								measurabilityProjects.length > 0 &&
								measurabilityProjects.map((project) => (
									<AnimatePresence
										key={project.id}
										mode="wait"
									>
										<motion.div
											key={project.id}
											initial={{ y: 10, opacity: 0 }}
											animate={{ y: 0, opacity: 1 }}
											exit={{ y: -10, opacity: 0 }}
											transition={{ duration: 0.2 }}
											style={{
												display: 'flex',
												flex: 1,
											}}
										>
											<ProjectCard
												cover={project?.image}
												type="measurability"
												projectType={project?.type}
												name={project?.name}
												active={project?.active}
												fullWidth
												supportedByLogo={
													project?.supportedBy
												}
												location={{
													countryCode:
														project?.country?.code ||
														NOT_APPLICABLE,
													place:
														project?.location?.place ||
														NOT_APPLICABLE,
													countryName:
														project?.country?.name ||
														NOT_APPLICABLE,
												}}
												onStatusChange={undefined}
												availableStock={
													project?.availableStock
												}
												sellPrice={project?.sellPrice}
												onClick={() =>
													navigate(
														`/catalog-measurabilities/${project.id}`
													)
												}
												showStatus={false}
												readonly={false}
											/>
										</motion.div>
									</AnimatePresence>
								))}
					</Box>
				)}

				{/* List mode */}
				{mode === 'list' && (
					<Flex
						wrap="wrap"
						gap="var(--mantine-spacing-sm)"
					>
						<AnimatePresence key="List mode" mode="wait">
							<motion.div
								key="empty"
								initial={{ y: 10, opacity: 0 }}
								animate={{ y: 0, opacity: 1 }}
								exit={{ y: -10, opacity: 0 }}
								transition={{ duration: 0.2 }}
								style={{ display: 'flex', flex: 1 }}
							>
								{Array.isArray(measurabilityProjects) && (
									<Datatable
										allColumns={columns}
										records={measurabilityProjects}
										columns={columns}
										onRowClick={(data: {
											record: { id: string };
										}) =>
											navigate(
												`/catalog-measurabilities/${data.record.id}`
											)
										}
									/>
								)}
							</motion.div>
						</AnimatePresence>
					</Flex>
				)}

				{/* Empty grid state */}
				{mode === 'grid' &&
					measurabilityProjects &&
					Array.isArray(measurabilityProjects) &&
					measurabilityProjects.length === 0 && (
						<Box className={classes.containerEmptyList}>
							<Box
								className={
									classes.containerTextsNoRecords
								}
							>
								<Image
									src={NoRecordsIcon}
									className={classes.iconNoRecords}
								/>
								<>
									<Text
										className={classes.titleNoRecords}
									>
										{t('global.datatable.noResultsFound')}
									</Text>
									<Text
										className={
											classes.descriptionNoRecords
										}
									>
										{t(
											'global.datatable.noResultsFoundDescription'
										)}
									</Text>
								</>
							</Box>
						</Box>
					)}

				{/* Pagination */}
				<Box className={classes['pagination-bar']}>
					<Pagination
						pageSize={measurabilityProjectsPageSize}
						totalItems={totalItems}
						activePage={measurabilityProjectsPage}
						onPageSizeChange={handleChangePaginationSize}
						onChange={handleChangePagination}
						total={totalPages ?? 0}
					/>
				</Box>
			</Paper>
		</Paper>
	);
};

export default MeasurabilityProjectsPublic;
